<template>
  <div
    class="pl-12 md:pl-16 overflow-y-auto bg-grey-1 transition scrollbar"
    :class="{ 'sidebar-closed': !sidebarOpen }"
  >
    <div class="flex-a justify-between p-x h-10 md:h-12">
      <nav v-if="!isGuided" aria-label="Breadcrumb" class="leading-none">
        <ol>
          <li class="inline">
            <RouterLink
              :to="{ name: 'home' }"
              class="focus-a"
              v-html="'Home / '"
            />
          </li>
          <li class="inline">
            <RouterLink
              :to="{ name: 'campus-category' }"
              class="focus-a"
              v-html="category.title"
            />
          </li>
        </ol>
      </nav>

      <nav v-if="isGuided" aria-label="Breadcrumb">
        <ol>
          <li class="inline">
            <RouterLink
              :to="{ name: 'home' }"
              class="focus-a"
              v-html="'Home / '"
            />
          </li>
          <li class="inline">
            <RouterLink
              :to="{ name: 'guided-tour' }"
              class="focus-a"
              v-html="`Guided tour`"
            />
          </li>
        </ol>
      </nav>

      <Button
        icon="ChevronIcon"
        type="button"
        iconsize="1.2rem"
        iconcolor="primary"
        :aria="sidebarOpen ? 'Hide sidebar' : 'Show sidebar'"
        :aria-expanded="sidebarOpen || false"
        aria-controls="sidebar-content"
        :iconrotation="sidebarOpen ? 'rotate-270' : 'rotate-90'"
        class="h-6"
        @buttonClick="sidebarOpen = !sidebarOpen"
      />
    </div>
    <Transition name="fade">
      <div v-show="sidebarOpen">
        <h1
          v-if="!isGuided"
          id="focus-target"
          class="flex-a p-x pt-3 pb-5 text-2xl font-medium b-b focus-b"
          v-html="location.title"
        />

        <div v-if="isGuided">
          <div
            role="img"
            :alt="guidedTourImage.alt"
            :style="`background-image: url(${guidedTourImage.src})`"
            class="h-40 bg-full bg-accent-1"
          />
          <ul class="flex-a h-12 p-x">
            <li>
              <Button
                :path="pager.prev"
                icon="ChevronIcon"
                iconsize="1.2rem"
                :iconcolor="pager.isFirst ? 'grey-2' : 'primary'"
                text="Prev 360"
                :tabindex="pager.isFirst ? -1 : 0"
                :textcolor="pager.isFirst ? 'grey-2' : 'primary'"
                :iconleft="true"
                aria="Previous panorama"
                iconrotation=""
                class="h-6 mr-8"
              />
            </li>
            <li>
              <Button
                :path="pager.next"
                icon="ChevronIcon"
                iconsize="1.2rem"
                :iconcolor="pager.isLast ? 'grey-2' : 'primary'"
                text="Next 360"
                :tabindex="pager.isLast ? -1 : 0"
                :textcolor="pager.isLast ? 'grey-2' : 'primary'"
                :iconright="true"
                aria="Next panorama"
                iconrotation="rotate-180"
                class="h-6"
              />
            </li>
          </ul>
        </div>

        <section
          id="sidebar-content"
          :aria-label="`${htmlDecode(location.title)}`"
          :aria-hidden="!sidebarOpen"
          class="transition"
          :class="{ hidden: !sidebarOpen }"
        >
          <SidebarItem
            v-if="isGuided"
            :post="post"
            :expanded="{ open: true }"
            :category="category"
            :is-guided="isGuided"
            @handleFocus="handleFocus"
            @handleBlur="handleBlur"
            @handleEnter="handleEnter"
            @handleEscape="handleEscape"
            @handleClick="handleClick"
          />

          <nav v-else aria-label="Panoramas and content spots">
            <ul>
              <li v-for="(child, i) in location.children" :key="child.slug">
                <SidebarItem
                  :post="child"
                  :expanded="{
                    open: openTab === i,
                    index: i,
                    openIndex: openTab
                  }"
                  :category="category"
                  @handleFocus="handleFocus"
                  @handleBlur="handleBlur"
                  @handleEnter="handleEnter"
                  @handleEscape="handleEscape"
                  @handleClick="handleClick"
                />
              </li>
            </ul>
          </nav>
        </section>
        <BtnBack
          v-if="!isGuided"
          iconsize="1.2rem"
          text="Back to map"
          textcolor="primary"
          iconcolor="primary"
          icon="ArrowIcon"
          rotate="rotate-180"
          margin="mr-2"
          class="p-x mt-12"
        />
      </div>
    </Transition>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { bus } from '@/main'
import Button from '@/components/buttons/Button.vue'

import SidebarItem from '@/components/SidebarItem.vue'
import BtnBack from '@/components/buttons/BtnBack.vue'

export default {
  name: 'Sidebar',

  components: {
    SidebarItem,
    BtnBack,
    Button
  },

  props: {
    post: {
      type: Object,
      default: () => {}
    },
    location: {
      type: Object,
      default: () => {}
    },
    category: {
      type: Object,
      default: () => {}
    },
    isGuided: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      sidebarOpen: true
    }
  },

  computed: {
    ...mapState(['touch', 'mobile', 'browser']),
    ...mapGetters(['guidedtour']),

    openTab() {
      // Return the index of the open sidebar item
      return this.location.children.findIndex(
        item => item.slug === this.post.slug
      )
    },

    guidedTourImage() {
      if (this.guidedtour.image)
        return {
          src: this.guidedtour.image.sizes.medium,
          alt: this.guidedtour.image.alt
        }
      return ''
    },

    pager() {
      const tourGroup = this.location.children

      const tourGroupLength = tourGroup.length

      const tourGroupSlugs = tourGroup.map(({ slug }) => slug)

      const currentPanorama = this.$route.params.panorama_id

      const currentIndex = tourGroupSlugs.indexOf(currentPanorama)

      const prev =
        currentIndex > 0
          ? tourGroupSlugs[currentIndex - 1]
          : tourGroupSlugs[tourGroupLength - 1]

      const next =
        currentIndex < tourGroupLength - 1
          ? tourGroupSlugs[currentIndex + 1]
          : tourGroupSlugs[0]

      const isFirst = currentIndex === 0
      const isLast = currentIndex === tourGroupLength - 1

      return {
        prev,
        next,
        currentIndex,
        currentPanorama,
        tourGroupSlugs,
        isLast,
        isFirst
      }
    }
  },

  watch: {
    sidebarOpen: {
      immediate: true,
      handler(value) {
        this.$store.commit('toggleSidebarState', value)
      }
    }
  },

  created() {
    if (this.mobile) {
      this.sidebarOpen = false
    }
  },

  methods: {
    handleFocus(slug) {
      // Event bus direct to Panorama.vue
      bus.$emit('panToHotSpot', slug)
    },

    handleBlur(slug) {
      // Event bus direct to Panorama.vue
      bus.$emit('closeHotSpot', slug)
    },

    handleEnter(slug) {
      // Emits to the parent Viewer.vue
      this.$emit('hotSpotClick', slug)
    },

    handleEscape(slug) {
      // Event bus direct to Panorama.vue
      bus.$emit('closeHotSpot', slug)
    },

    handleClick(slug) {
      if (this.mobile) {
        this.$emit('hotSpotClick', slug)
      }

      // Because Safari doen't 'focus' a button on click
      if (!this.mobile && this.browser === 'Safari') {
        this.handleFocus(slug)
      }
    }
  }
}
</script>
